<template>
	<div class="app-header">
		<!-- **************************************************************************************************** Top menu -->
		<PMenubar
			:model="mainMenuItems"
			class="flex justify-content-between border-noround border-none"
			v-if="topMenu"
		>
			<!-- Title -->
			<template #start>
				<div class="flex align-items-center">
					<span class="text-4xl font-bold text-primary mx-2">PowerX</span>
					<PTag :severity="appModeStatus">{{ appMode }}</PTag>
				</div>
			</template>

			<!-- Menu items -->
			<template #item="{ item, props, hasSubmenu, root }">
				<div>
					<a
						class="flex align-items-center text-primary"
						v-bind="props.action"
					>
						<span
							:class="item.icon"
							class="mr-2"
							style="color: grey"
						/>
						<span>{{ item.label }}</span>
						<PBadge
							:class="{ 'ml-auto': !root, 'ml-2': root }"
							:value="item.badge"
							v-if="item.badge"
						/>
						<span
							class="ml-auto border-1 surface-border border-round surface-100 text-xs p-1"
							v-if="item.shortcut"
						>{{ item.shortcut }}</span>
						<i
							:class="['pi pi-angle-down', { 'pi-angle-down ml-2': root, 'pi-angle-right ml-auto': !root }]"
							v-if="hasSubmenu"
						></i>
					</a>
				</div>
			</template>
		</PMenubar>

		<!-- ***************************************************************************************************** Sidebar -->
		<div
			class="card flex justify-content-start"
			v-if="!topMenu"
		>
			<PSidebar
				header="PowerX"
				v-model:visible="showSidebar"
			>
				<!-- Close icon -->
				<template #closeicon>
					<div class="text-primary pi pi-times"></div>
				</template>

				<!-- Sidebar Menu -->
				<PPanelMenu :model="mainMenuItems" />
			</PSidebar>
			<!-- Title while showing the sidebar -->
			<div class="flex align-items-center justify-content-between w-full">
				<div class="flex flex-row">
					<!-- Bars icons to toggle the sidebar -->
					<PButton
						@click="showSidebar = true"
						icon="pi pi-bars"
						rounded
						text
					/>
					<span class="text-4xl font-bold text-primary mx-2">PowerX</span>
					<div class="flex align-items-center">
						<PTag :severity="appModeStatus">{{ appMode }}</PTag>
					</div>
				</div>
				<!-- Logout while showing the sidebar -->
				<div v-if="!topMenu">
					<PButton
						:icon="logoutItems.icon"
						:label="logoutItems.label"
						@click="logoutItems.command()"
						text
					/>
				</div>
			</div>
		</div>
		<PDivider
			class="my-2"
			v-if="!topMenu"
		/>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Utils from '@/utils/Utils'
import Constants from '@/helpers/Constants'
import PB from '@/brokers/PersistencyBroker'
import EventBus from '@/utils/EventBus'
import store from '@/store'
import AuthManager from '@/utils/AuthManager'

export default defineComponent({
	components: {
	},

	data() {
		return {
			appMode: 'Live',
			topMenu: true,
			showSidebar: false,
			stickySidebar: false,
			activeIndex: 0,
			mainMenuItems: [
				{
					label: 'Dashboard',
					icon: 'pi pi-th-large',
					command: () => { this.goto('/') }
				},
				{
					label: 'Data',
					icon: 'pi pi-chart-bar',
					items: [
						{
							label: 'Units',
							icon: 'mdi mdi-scale',
							command: () => {
								this.goto('units')
							}
						},
						{
							label: 'Signals list',
							icon: 'pi pi-list',
							command: () => {
								this.goto('mda_signals')
							}
						},
						{
							label: 'Readings',
							items: [
								{
									label: 'Solar Data',
									icon: 'pi pi-bolt',
									command: () => {
										this.goto('solar_readings')
									}
								},
								{
									label: 'Meters Data',
									icon: 'pi pi-spinner',
									command: () => {
										this.goto('mda_readings')
									}

								},
								{
									label: 'DCS Data',
									icon: 'pi pi-arrow-right-arrow-left',
									command: () => {
										this.goto('dcs_readings')
									}

								},
							],
						},
					]
				},
				{
					label: 'Contracts',
					icon: 'pi pi-verified',
					items: [
						{
							label: 'Financial Inputs',
							icon: 'pi pi-dollar',
							command: () => {
								this.goto('financial_inputs')
							}
						},
						{
							label: 'Coefficients',
							icon: 'pi pi-tag',
							command: () => {
								this.goto('coefficients')
							}
						},
						// {
						// 	label: 'Formulas',
						// 	icon: 'pi pi-wrench',
						// },
					]
				},
				{
					label: 'Tariff Calculations',
					icon: 'pi pi-calculator',
					command: () => {
						this.goto('formulas')
					}
				},
				{
					label: 'Performance',
					icon: 'pi pi-chart-line',
					visible: store.getters.can('ppm', 'read'),
					command: () => {
						this.goto('performance')
					}
				},

				{
					label: 'More ...',
					icon: 'pi pi-plus',
					items: [
						{
							label: 'Project',
							icon: 'pi-calendar-times',
							command: () => {
								this.goto('project')
							}
						},
						{
							label: 'Reports',
							icon: 'pi pi-file',
							command: () => {
								this.goto('reports')
							}
						},
						{
							label: 'Health Check',
							icon: 'pi pi-heart',
							visible: store.getters.can('health_check', 'read'),
							command: () => {
								this.goto('health-check')
							}
						},
						{
							label: 'Manual Entries',
							icon: 'pi pi-pencil',
							visible: store.getters.can('manual_entries', 'read'),
							command: () => {
								this.goto('manual-entries')
							}
						},
						{
							label: 'Logs',
							icon: 'pi pi-book',
							command: () => {
								this.goto('logs')
							}
						},
						{
							label: 'Documentation',
							icon: 'pi pi-file-edit',
							command: () => {
								this.goto('documentation')
							}
						},
						{
							label: 'Users',
							icon: 'pi pi-users',
							visible: store.getters.can('users', 'read'),
							command: () => {
								this.goto('users')
							}
						},
						{
							label: 'Roles',
							icon: 'pi pi-shield',
							visible: store.getters.can('roles', 'read'),
							command: () => {
								this.goto('roles')
							}
						},
						{
							separator: true
						},
						{
							label: 'App Mode',
							icon: 'pi pi-compass',
							command: () => {
								this.goto('app_mode')
							}
						},
						{
							separator: true
						},
						{
							label: 'Settings',
							icon: 'pi pi-cog',
							command: () => {
								this.goto('settings')
							}
						},
						{
							label: 'About',
							icon: 'pi pi-info-circle',
							command: () => {
								this.goto('about')
							}
						},
					]
				},
				{
					label: 'Log out',
					icon: 'pi pi-power-off',
					command: () => {
						this.doLogOut()
					},
				},
			],
			logoutItems: {
				label: 'Logout',
				icon: 'pi pi-power-off',
				command: () => {
					this.doLogOut()
				},
			}
		}
	},

	created() {
		// Listeners
		//@ts-ignore
		EventBus.on(Constants.EVENTS.SET_TOP_MENU_POSITION, this.setTopMenuPosition)
		//@ts-ignore
		EventBus.on(Constants.EVENTS.APP_MODE_CHANGED, this.setAppMode)

		// Get the latest top menu position
		const topMenuPosition = PB.get(Constants.LOCAL_STORAGE_IDS.MENU_POSITION)
		this.topMenu = topMenuPosition ? topMenuPosition == 'Top' ? true : false : true

		// Sync Audit Mode
		const auditMode = PB.get(Constants.LOCAL_STORAGE_IDS.APP_MODE)
		this.appMode = auditMode === 'Audit' ? 'Audit Mode' : 'Live'
		store.dispatch('setAppMode', this.appMode.split(' ')[0])
	},

	computed: {
		appModeStatus() {
			// @ts-ignore
			return this.appMode === 'Audit Mode' ? 'warning' : 'success'
		}
	},

	methods: {
		async doLogOut() {
			await AuthManager.logout()
			Utils.navigateTo('login', true)
		},

		setTopMenuPosition(topMenuPosition: boolean) {
			this.topMenu = topMenuPosition
		},

		setAppMode(appMode: string) {
			this.appMode = appMode === 'Audit' ? 'Audit Mode' : 'Live'
		},

		goto(destination: string) {
			if (this.showSidebar && !this.stickySidebar)
				this.showSidebar = false
			Utils.navigateTo(destination)
		},
	}
})
</script>

<style scoped>
.app-header {
	background: var(--surface-section, #ffffff);
}

h1 {
	font-weight: 500;
	font-size: 2.6rem;
}

h3 {
	font-size: 1.2rem;
}
</style>